import React, { useEffect, useMemo, useState } from "react";

/**
 * Super Admin System Health — production scope
 *
 * Purpose:
 * - Frontend page for Super Admin operational safety signals
 * - Platform governance and workflow integrity page
 *
 * Current scope:
 * - Reads from the Super Admin system health endpoint
 * - Aggregate and workflow-safety data only
 * - No infrastructure monitoring
 * - No billing
 * - No PHI display
 *
 * Endpoint:
 * - GET /api/super-admin/system-health
 */

type HealthTone = "warning" | "neutral" | "action";

type HealthSignal = {
  id: string;
  title: string;
  value: string;
  description: string;
  tone: HealthTone;
};

type HealthSummary = {
  pendingApprovals: number;
  oldPendingApprovals: number;
  approvedClinics: number;
  clinicsWithoutAdmin: number;
  publicListingIssues: number;
  stuckRequests: number;
  auditEventsToday: number;
};

type ClinicSafetyItem = {
  clinicId: number;
  clinicName: string | null;
  governanceStatus?: string | null;
  approvedAt?: string | null;
  cityName?: string | null;
  postalCode?: string | null;
};

type PublicListingIssueItem = ClinicSafetyItem & {
  missing?: string[];
};

type OldPendingApprovalItem = {
  accessRequestId: number;
  userId: number | null;
  requestedRole: string | null;
  clinicAdminAction: string | null;
  submittedAt: string | null;
  clinicId: number | null;
  existingClinicName: string | null;
  newClinicName: string | null;
};

type StuckRequestItem = {
  requestId: number;
  clinicId: number | null;
  clinicName: string | null;
  familyDoctorProfileId: number | null;
  statusName: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
};

type HealthResponse = {
  summary?: Partial<HealthSummary>;
  lists?: {
    clinicsWithoutAdmin?: ClinicSafetyItem[];
    publicListingIssues?: PublicListingIssueItem[];
    oldPendingApprovals?: OldPendingApprovalItem[];
    stuckRequests?: StuckRequestItem[];
  };
  error?: string;
};

const EMPTY_SUMMARY: HealthSummary = {
  pendingApprovals: 0,
  oldPendingApprovals: 0,
  approvedClinics: 0,
  clinicsWithoutAdmin: 0,
  publicListingIssues: 0,
  stuckRequests: 0,
  auditEventsToday: 0,
};

export default function SuperAdminSystemHealthPage() {
  const [summary, setSummary] = useState<HealthSummary>(EMPTY_SUMMARY);
  const [clinicsWithoutAdmin, setClinicsWithoutAdmin] = useState<
    ClinicSafetyItem[]
  >([]);
  const [publicListingIssues, setPublicListingIssues] = useState<
    PublicListingIssueItem[]
  >([]);
  const [oldPendingApprovals, setOldPendingApprovals] = useState<
    OldPendingApprovalItem[]
  >([]);
  const [stuckRequests, setStuckRequests] = useState<StuckRequestItem[]>([]);

  const [staleApprovalDays, setStaleApprovalDays] = useState("7");
  const [stuckRequestDays, setStuckRequestDays] = useState("7");
  const [limit, setLimit] = useState("20");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();

    params.set("staleApprovalDays", clampStringNumber(staleApprovalDays, 7, 1, 90));
    params.set("stuckRequestDays", clampStringNumber(stuckRequestDays, 7, 1, 90));
    params.set("limit", clampStringNumber(limit, 20, 1, 50));

    return params.toString();
  }, [limit, staleApprovalDays, stuckRequestDays]);

  const signals: HealthSignal[] = useMemo(
    () => [
      {
        id: "pending-approvals",
        title: "Pending Approvals",
        value: formatCount(summary.pendingApprovals),
        description: "Clinic Admin onboarding requests waiting for review.",
        tone: summary.pendingApprovals > 0 ? "action" : "neutral",
      },
      {
        id: "clinics-without-admin",
        title: "Clinics Without Admin",
        value: formatCount(summary.clinicsWithoutAdmin),
        description: "Approved clinics with no active Clinic Admin assigned.",
        tone: summary.clinicsWithoutAdmin > 0 ? "warning" : "neutral",
      },
      {
        id: "public-listing-issues",
        title: "Public Listing Issues",
        value: formatCount(summary.publicListingIssues),
        description:
          "Approved clinics missing address, phone, coordinates, or other public eligibility fields.",
        tone: summary.publicListingIssues > 0 ? "warning" : "neutral",
      },
      {
        id: "old-pending-approvals",
        title: "Old Pending Approvals",
        value: formatCount(summary.oldPendingApprovals),
        description:
          "Clinic onboarding requests that remained pending beyond the review threshold.",
        tone: summary.oldPendingApprovals > 0 ? "action" : "neutral",
      },
      {
        id: "stuck-patient-requests",
        title: "Stuck Requests",
        value: formatCount(summary.stuckRequests),
        description:
          "Verified patient requests beyond the expected clinic action window.",
        tone: summary.stuckRequests > 0 ? "warning" : "neutral",
      },
      {
        id: "audit-events-today",
        title: "Audit Events Today",
        value: formatCount(summary.auditEventsToday),
        description:
          "Governance audit events captured today in the current audit log.",
        tone: "neutral",
      },
    ],
    [summary]
  );

  async function loadHealth(mode: "initial" | "refresh" = "initial") {
    try {
      if (mode === "initial") {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError(null);

      const res = await fetch(`/api/super-admin/system-health?${queryString}`, {
        method: "GET",
        credentials: "include",
      });

      const json = (await res.json().catch(() => ({}))) as HealthResponse;

      if (!res.ok) {
        throw new Error(
          json?.error || `Unable to load system health (${res.status})`
        );
      }

      setSummary({
        ...EMPTY_SUMMARY,
        ...(json.summary || {}),
      });

      setClinicsWithoutAdmin(
        Array.isArray(json.lists?.clinicsWithoutAdmin)
          ? json.lists.clinicsWithoutAdmin
          : []
      );

      setPublicListingIssues(
        Array.isArray(json.lists?.publicListingIssues)
          ? json.lists.publicListingIssues
          : []
      );

      setOldPendingApprovals(
        Array.isArray(json.lists?.oldPendingApprovals)
          ? json.lists.oldPendingApprovals
          : []
      );

      setStuckRequests(
        Array.isArray(json.lists?.stuckRequests)
          ? json.lists.stuckRequests
          : []
      );
    } catch (e: any) {
      setError(e?.message || "Unable to load system health.");
      setSummary(EMPTY_SUMMARY);
      setClinicsWithoutAdmin([]);
      setPublicListingIssues([]);
      setOldPendingApprovals([]);
      setStuckRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadHealth("initial");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalWarnings =
    summary.oldPendingApprovals +
    summary.clinicsWithoutAdmin +
    summary.publicListingIssues +
    summary.stuckRequests;

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 10px",
            borderRadius: 999,
            background: "#eaf4ff",
            border: "1px solid #cfe6ff",
            color: "#1669b3",
            fontSize: 12,
            fontWeight: 650,
            marginBottom: 10,
          }}
        >
          System safety signals
        </div>

        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 650,
            color: "#102a43",
            letterSpacing: "-0.02em",
          }}
        >
          System Health
        </h1>

        <p
          style={{
            marginTop: 8,
            color: "#6b7a94",
            lineHeight: 1.55,
            maxWidth: 840,
            fontSize: 14,
            fontWeight: 450,
          }}
        >
          Operational safety view for platform governance. This page focuses on
          governance and workflow integrity: clinics without admins, approved
          clinics that are not publicly searchable, stale approvals, and stuck
          patient-request workflows.
        </p>
      </div>

      {/* Status banner */}
      <section
        style={{
          padding: 18,
          borderRadius: 18,
          border: totalWarnings > 0 ? "1px solid #f4d18f" : "1px solid #b9dcff",
          background: totalWarnings > 0 ? "#fffaf0" : "#f7fbff",
          boxShadow: "0 8px 24px rgba(15, 80, 135, 0.04)",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 650,
            color: totalWarnings > 0 ? "#92400e" : "#0f5087",
            letterSpacing: "-0.01em",
          }}
        >
          {loading
            ? "Checking system health…"
            : totalWarnings > 0
              ? "Health endpoint connected — action signals found"
              : "Health endpoint connected — no current action signals"}
        </div>

        <p
          style={{
            marginTop: 6,
            marginBottom: 0,
            color: "#6b7a94",
            fontSize: 13,
            lineHeight: 1.55,
            maxWidth: 880,
            fontWeight: 450,
          }}
        >
          This page reads aggregate and non-PHI safety signals from the Super
          Admin system health endpoint. It is not infrastructure monitoring and
          does not expose patient clinical information.
        </p>
      </section>

      {/* Controls */}
      <section
        style={{
          padding: 14,
          borderRadius: 18,
          border: "1px solid #d1d9e6",
          background: "#ffffff",
          boxShadow: "0 8px 24px rgba(15, 80, 135, 0.04)",
          marginBottom: 18,
          display: "grid",
          gridTemplateColumns:
            "minmax(180px, 1fr) minmax(180px, 1fr) minmax(120px, 160px) auto",
          gap: 10,
          alignItems: "end",
        }}
      >
        <label style={filterLabelStyle}>
          Stale approval days
          <input
            value={staleApprovalDays}
            onChange={(e) => setStaleApprovalDays(e.target.value)}
            inputMode="numeric"
            style={filterControlStyle}
          />
        </label>

        <label style={filterLabelStyle}>
          Stuck request days
          <input
            value={stuckRequestDays}
            onChange={(e) => setStuckRequestDays(e.target.value)}
            inputMode="numeric"
            style={filterControlStyle}
          />
        </label>

        <label style={filterLabelStyle}>
          List limit
          <input
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            inputMode="numeric"
            style={filterControlStyle}
          />
        </label>

        <button
          type="button"
          onClick={() => loadHealth("refresh")}
          disabled={loading || refreshing}
          style={{
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid #1669b3",
            background: "#1669b3",
            color: "#ffffff",
            fontSize: 13,
            fontWeight: 650,
            cursor: loading || refreshing ? "not-allowed" : "pointer",
            opacity: loading || refreshing ? 0.65 : 1,
            whiteSpace: "nowrap",
          }}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      {error ? (
        <div
          style={{
            marginBottom: 18,
            padding: 14,
            borderRadius: 14,
            border: "1px solid #fecaca",
            background: "#fff1f2",
            color: "#991b1b",
            fontSize: 13,
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* Signal cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      >
        {signals.map((signal) => (
          <HealthCard key={signal.id} signal={signal} loading={loading} />
        ))}
      </div>

      {/* Health lists */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: 16,
        }}
      >
        <HealthListSection
          title="Clinics Without Admin"
          description="Approved clinics that do not currently have an active Clinic Admin role."
          emptyText="No approved clinics without an active Clinic Admin."
          items={clinicsWithoutAdmin.map((clinic) => ({
            key: `clinic-admin-${clinic.clinicId}`,
            title: clinic.clinicName || `Clinic #${clinic.clinicId}`,
            meta: [
              `Clinic ID: ${clinic.clinicId}`,
              clinic.cityName ? `City: ${clinic.cityName}` : null,
              clinic.approvedAt ? `Approved: ${formatTimestamp(clinic.approvedAt)}` : null,
            ],
          }))}
          loading={loading}
        />

        <HealthListSection
          title="Public Listing Issues"
          description="Approved clinics missing required public listing fields or coordinates."
          emptyText="No approved clinic public listing issues found."
          items={publicListingIssues.map((clinic) => ({
            key: `public-${clinic.clinicId}`,
            title: clinic.clinicName || `Clinic #${clinic.clinicId}`,
            meta: [
              `Clinic ID: ${clinic.clinicId}`,
              clinic.cityName ? `City: ${clinic.cityName}` : null,
              clinic.missing?.length
                ? `Missing: ${clinic.missing.join(", ")}`
                : "Missing: —",
            ],
          }))}
          loading={loading}
        />

        <HealthListSection
          title="Old Pending Approvals"
          description="Pending Clinic Admin access requests older than the selected stale approval threshold."
          emptyText="No stale pending approvals found."
          items={oldPendingApprovals.map((request) => ({
            key: `approval-${request.accessRequestId}`,
            title:
              request.existingClinicName ||
              request.newClinicName ||
              `Access request #${request.accessRequestId}`,
            meta: [
              `AccessRequest ID: ${request.accessRequestId}`,
              request.clinicAdminAction
                ? `Action: ${request.clinicAdminAction}`
                : null,
              request.submittedAt
                ? `Submitted: ${formatTimestamp(request.submittedAt)}`
                : null,
            ],
          }))}
          loading={loading}
        />

        <HealthListSection
          title="Stuck Patient Requests"
          description="Verified patient requests beyond the expected action window. Patient identity is intentionally not shown here."
          emptyText="No stuck patient-request workflows found."
          items={stuckRequests.map((request) => ({
            key: `stuck-${request.requestId}`,
            title: request.clinicName || `Clinic #${request.clinicId || "—"}`,
            meta: [
              `Request ID: ${request.requestId}`,
              request.statusName ? `Status: ${request.statusName}` : null,
              request.submittedAt
                ? `Submitted: ${formatTimestamp(request.submittedAt)}`
                : null,
            ],
          }))}
          loading={loading}
        />
      </div>

      <HealthSection
        title="Additional Platform Areas"
        description="These areas are managed outside the current System Health view."
        items={[
          "Infrastructure monitoring and deployment telemetry",
          "Billing and subscription state management",
          "Advanced patient request analytics",
          "Provider repair workflows",
          "Revenue, MRR, churn, or payment reporting",
        ]}
        muted
      />
    </div>
  );
}

/* ---------- Local UI components ---------- */

const filterLabelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  color: "#64748b",
  fontSize: 12,
  fontWeight: 650,
  minWidth: 0,
};

const filterControlStyle: React.CSSProperties = {
  width: "100%",
  minWidth: 0,
  borderRadius: 12,
  border: "1px solid #d1d9e6",
  background: "#ffffff",
  color: "#102a43",
  padding: "9px 10px",
  fontSize: 13,
  fontWeight: 500,
  outline: "none",
  boxSizing: "border-box",
};

function HealthCard({
  signal,
  loading,
}: {
  signal: HealthSignal;
  loading: boolean;
}) {
  const toneStyles: Record<
    HealthSignal["tone"],
    {
      background: string;
      border: string;
      accent: string;
      valueColor: string;
    }
  > = {
    neutral: {
      background: "#ffffff",
      border: "#d1d9e6",
      accent: "#1669b3",
      valueColor: "#102a43",
    },
    action: {
      background: "#f7fbff",
      border: "#b9dcff",
      accent: "#1669b3",
      valueColor: "#0f5087",
    },
    warning: {
      background: "#fffaf0",
      border: "#f4d18f",
      accent: "#b45309",
      valueColor: "#92400e",
    },
  };

  const selectedTone = toneStyles[signal.tone];

  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        padding: 16,
        borderRadius: 16,
        border: `1px solid ${selectedTone.border}`,
        background: selectedTone.background,
        boxShadow: "0 8px 24px rgba(15, 80, 135, 0.05)",
        minHeight: 142,
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: selectedTone.accent,
          opacity: 0.9,
        }}
      />

      <div
        style={{
          fontSize: 13,
          fontWeight: 650,
          color: "#6b7a94",
          marginBottom: 8,
        }}
      >
        {signal.title}
      </div>

      <div
        style={{
          fontSize: 30,
          fontWeight: 650,
          color: selectedTone.valueColor,
          letterSpacing: "-0.03em",
          lineHeight: 1,
        }}
      >
        {loading ? "—" : signal.value}
      </div>

      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: "#6b7a94",
          fontWeight: 500,
          lineHeight: 1.45,
        }}
      >
        {signal.description}
      </div>
    </section>
  );
}

function HealthListSection({
  title,
  description,
  items,
  emptyText,
  loading,
}: {
  title: string;
  description: string;
  items: {
    key: string;
    title: string;
    meta: Array<string | null>;
  }[];
  emptyText: string;
  loading: boolean;
}) {
  return (
    <section
      style={{
        padding: 20,
        borderRadius: 18,
        border: "1px solid #d1d9e6",
        background: "#ffffff",
        boxShadow: "0 8px 24px rgba(15, 80, 135, 0.05)",
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 650,
          color: "#102a43",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </div>

      <p
        style={{
          marginTop: 6,
          marginBottom: 14,
          color: "#6b7a94",
          fontSize: 13,
          lineHeight: 1.55,
          fontWeight: 450,
        }}
      >
        {description}
      </p>

      {loading ? (
        <div style={emptyStateStyle}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={emptyStateStyle}>{emptyText}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item) => (
            <div
              key={item.key}
              style={{
                padding: 12,
                borderRadius: 14,
                border: "1px solid #e5edf5",
                background: "#f8fafc",
              }}
            >
              <div
                style={{
                  color: "#102a43",
                  fontSize: 13,
                  fontWeight: 650,
                  lineHeight: 1.35,
                }}
              >
                {item.title}
              </div>

              <div
                style={{
                  marginTop: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px 10px",
                }}
              >
                {item.meta.filter(Boolean).map((meta) => (
                  <span
                    key={meta || ""}
                    style={{
                      color: "#64748b",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {meta}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function HealthSection({
  title,
  description,
  items,
  muted = false,
}: {
  title: string;
  description: string;
  items: string[];
  muted?: boolean;
}) {
  return (
    <section
      style={{
        marginTop: 18,
        padding: 20,
        borderRadius: 18,
        border: muted ? "1px dashed #cbd5e1" : "1px solid #d1d9e6",
        background: muted ? "#f8fafc" : "#ffffff",
        boxShadow: muted ? "none" : "0 8px 24px rgba(15, 80, 135, 0.05)",
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 650,
          color: "#102a43",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </div>

      <p
        style={{
          marginTop: 6,
          marginBottom: 14,
          color: "#6b7a94",
          fontSize: 13,
          maxWidth: 760,
          lineHeight: 1.55,
          fontWeight: 450,
        }}
      >
        {description}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item) => (
          <div
            key={item}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 9,
              color: "#102a43",
              fontSize: 13,
              lineHeight: 1.45,
              fontWeight: 500,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: muted ? "#94a3b8" : "#1669b3",
                marginTop: 6,
                flex: "0 0 auto",
              }}
            />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const emptyStateStyle: React.CSSProperties = {
  padding: 12,
  borderRadius: 14,
  border: "1px dashed #cbd5e1",
  background: "#f8fafc",
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.5,
  fontWeight: 500,
};

function clampStringNumber(
  value: string,
  fallback: number,
  min: number,
  max: number
): string {
  const n = Number.parseInt(String(value), 10);
  if (!Number.isFinite(n)) return String(fallback);
  return String(Math.min(Math.max(n, min), max));
}

function formatCount(value: number | null | undefined): string {
  const n = Number(value || 0);
  return n.toLocaleString("en-CA");
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}